import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

/**
 * Consumer-side counterpart to usePendingInquiries.
 * Returns:
 *   total          — every inquiry the consumer has sent (still pending/accepted/etc.)
 *   pendingAction  — count of offers awaiting buyer action (status=pending_payment).
 *                    Drives the gold blinking "Pay now" pin on the consumer Overview.
 */
export function useConsumerInquiries() {
  const { getAuthHeaders, user } = useAuth();
  const [total, setTotal] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [pendingAction, setPendingAction] = useState(0);
  const timerRef = useRef(null);

  const fetchCounts = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const res = await fetch(`${API}/messages/inquiries/mine`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const items = data.inquiries || [];
      setTotal(items.length);
      setPendingAction(items.filter((i) => i.status === 'pending_payment').length);
      // "Open" = anything still requiring buyer attention or in progress.
      setOpenCount(items.filter((i) =>
        ['pending', 'pending_payment', 'active', 'delivered', 'revision_requested'].includes(i.status),
      ).length);
    } catch {
      /* leave stale */
    }
  }, [getAuthHeaders, user?.user_id]);

  useEffect(() => {
    if (!user?.user_id) return undefined;
    fetchCounts();
    timerRef.current = setInterval(fetchCounts, POLL_MS);
    const onFocus = () => fetchCounts();
    window.addEventListener('focus', onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchCounts, user?.user_id]);

  return { total, openCount, pendingAction, refresh: fetchCounts };
}
